/**
 * Distribution (build variant) utilities. Works in both main and renderer.
 *
 * The active variant is baked into the packaged `package.json` at build time
 * via electron-builder `extraMetadata` (see `build/build-variants.cjs` and
 * `electron-builder.config.js`): `{ "distribution": "legacy" | "highlight" }`.
 *
 * Only the `distribution` is persisted — the CDN namespace prefix is *derived*
 * from it via the canonical `distributionCdnPrefix()` in `@highlight/common`,
 * so the variant→prefix mapping lives in exactly one place. `legacy` builds (or
 * any build missing the field, e.g. unpackaged dev) default to `legacy`, which
 * yields the existing unprefixed URLs.
 */
import { isRenderer } from '@highlight/ipc'
import { isLegacyDistribution, normalizeDistribution } from '@highlight/common'
import { isDev, isMac } from '@shared/utils/platform'

function readPackagedDistribution(): string | undefined {
  if (isRenderer) {
    if (typeof window === 'undefined') return undefined
    return window.highlight?.distribution
  }

  // Main process: read from the packaged app's package.json.
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const { app } = require('electron')
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const path = require('path')
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  const fs = require('fs')

  try {
    const packageJsonPath = path.join(app.getAppPath(), 'package.json')
    const packageJson = JSON.parse(fs.readFileSync(packageJsonPath, 'utf-8'))
    return packageJson.distribution
  } catch {
    return undefined
  }
}

/**
 * Variant this build was produced as (`legacy` | `highlight`); defaults to
 * `legacy`. Routed through the canonical `normalizeDistribution()` so missing
 * (dev/unpackaged) or unknown values are contained at this boundary as
 * `legacy` rather than propagating an unrecognized string. The CDN namespace is
 * derived from the result via `@highlight/common`'s `distributionCdnUrl()` /
 * `distributionCdnPrefix()` at the call site (e.g. the auto-updater feed URL),
 * so the variant→namespace mapping lives in one place.
 */
export function getInstalledDistribution(): string {
  return normalizeDistribution(readPackagedDistribution())
}

/**
 * Whether this install runs under a different OS identity than the Medal-era
 * `legacy` app. Only macOS `highlight` builds change Apple team and bundle id.
 * Windows `highlight` keeps the existing NSIS `appId`, so first-run identity
 * migration (auth clear, screenshot purge, IPC key rename) must not run there.
 */
export function usesNewSigningIdentity(
  platform: string = process.platform,
  distribution: string | null | undefined = getInstalledDistribution(),
): boolean {
  return platform === 'darwin' && !isLegacyDistribution(distribution)
}

/**
 * Whether this is a macOS build signed by the old Apple team (Medal B.V.) —
 * the `legacy` distribution. Publishing normally stops after the migration
 * switch is disabled, so the app directs these users to reinstall the
 * Highlight US Inc build.
 *
 * Unpackaged dev runs fall back to `legacy` and are excluded. Windows legacy
 * installs remain on the unprefixed feed while migration publishing is enabled.
 */
export function isStrandedLegacyMacBuild(): boolean {
  return isMac && !isDev && isLegacyDistribution(getInstalledDistribution())
}
